/**
 * Optimized Image Converter - Lazy Loading
 * Only loads heic2any when HEIC images are actually uploaded
 */

let heicToLib = null;

// Lazy load heic-to only when needed
async function loadHeicConverter() {
  if (!heicToLib) {
    console.log('🔄 Loading heic-to library (HEIC image detected)');
    const mod = await import("heic-to");
    heicToLib = mod.heicTo;
  }
  return heicToLib;
}

// Check if file is HEIC format
function isHeicFormat(file) {
  return file.type === "image/heic" || 
         file.type === "image/heif" || 
         file.name.toLowerCase().endsWith('.heic') || 
         file.name.toLowerCase().endsWith('.heif');
}

export async function processImage(file) {
  return new Promise(async (resolve, reject) => {
    try {
      let convertedFile = file;

      // Only load heic2any if we actually have a HEIC file
      if (isHeicFormat(file)) {
        console.log('🖼️ HEIC image detected, loading converter...');
        const heicTo = await loadHeicConverter();

        const blob = await heicTo({
          blob: file,
          type: "image/jpeg",
          quality: 0.8,
        });
        convertedFile = new File(
          [blob], 
          file.name.replace(/\.heic|\.heif/i, ".jpg"), 
          { type: "image/jpeg" }
        );
        // console.log('HEIC conversion complete');
      } else {
        // console.log('Regular image format, no conversion needed');
      }

      const reader = new FileReader();
      reader.readAsDataURL(convertedFile);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvasPreview = document.createElement("canvas");
          const canvasUpload = document.createElement("canvas");
          const ctxPreview = canvasPreview.getContext("2d");
          const ctxUpload = canvasUpload.getContext("2d");
          
          // UI Preview Image
          // Changing this will enlarge smaller files (blurry but visible), files larger than 1024 will cap at 1024
          const previewSize = 1024;
          const previewScale = Math.min(previewSize / img.width, previewSize / img.height);
          canvasPreview.width = img.width * previewScale;
          canvasPreview.height = img.height * previewScale;
          ctxPreview.drawImage(img, 0, 0, canvasPreview.width, canvasPreview.height);
          console.log(`📸 Original: ${img.width}x${img.height}, Preview: ${canvasPreview.width}x${canvasPreview.height}`);

          // AI Processing Image (1024x1024)
          const targetSize = 1024;
          if (img.width > targetSize || img.height > targetSize) {
            const scale = Math.min(targetSize / img.width, targetSize / img.height);
            canvasUpload.width = img.width * scale;
            canvasUpload.height = img.height * scale;
          } else {
            canvasUpload.width = img.width;
            canvasUpload.height = img.height;
          }
          ctxUpload.drawImage(img, 0, 0, canvasUpload.width, canvasUpload.height);
          console.log(`📤 Upload (to AI/DB): ${canvasUpload.width}x${canvasUpload.height}`);

          // Convert UI Preview Image to JPG
          canvasPreview.toBlob((blobPreview) => {
            if (!blobPreview) {
              reject(new Error("UI preview image processing failed."));
              return;
            }
            const previewImage = new File(
              [blobPreview], 
              "preview_" + convertedFile.name.replace(/\.(png|jpeg|jpg|gif|bmp|webp)$/i, "") + ".jpg", 
              { type: "image/jpeg" }
            );
            
            // Convert AI Processing Image to JPG
            canvasUpload.toBlob((blobUpload) => {
              if (!blobUpload) {
                reject(new Error("Upload image processing failed."));
                return;
              }
              const uploadImage = new File(
                [blobUpload], 
                "upload_" + convertedFile.name.replace(/\.(png|jpeg|jpg|gif|bmp|webp)$/i, "") + ".jpg", 
                { type: "image/jpeg" }
              );
              
              resolve({ previewImage, uploadImage });
            }, "image/jpeg", 0.8);
          }, "image/jpeg", 0.8);
        };
      };
    } catch (error) {
      console.error('❌ Image processing failed:', error);
      reject(error);
    }
  });
}

// Cleanup function to terminate any workers (if needed)
export function cleanupImageProcessing() {
  // heic2any workers should cleanup automatically when module is unloaded
  // but we can force cleanup if needed
}